import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Play, Loader2, ChevronLeft, ListMusic, ListPlus, ScrollText } from 'lucide-react';
import LyricsModal from '../../components/LyricsModal';
import PublicLayout from '../../components/public/PublicLayout';
import { useMusicPlayer } from '../../contexts/MusicPlayerContext';
import SEO from '../../components/SEO';
import ShareButtons from '../../components/ShareButtons';
import { PUBLIC_BASE } from './config';
import ContentCommentSection from '../../components/public/ContentCommentSection';
import LikeButton from '../../components/public/LikeButton';
import { GENRE_MAP } from '../../data/genres';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

function PublicAlbumDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addBulkToPlaylist, playImmediate } = useMusicPlayer();

  const [album, setAlbum] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [lyricsModal, setLyricsModal] = useState(null);

  useEffect(() => {
    const fetchAlbum = async () => {
      try {
        const res = await axios.get(`/public/albums/${id}`);
        setAlbum(res.data.album || res.data);
        setSongs(res.data.songs || []);
      } catch (err) {
        console.error('앨범 상세 로드 실패:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAlbum();
  }, [id]);

  const buildTrack = (song) => ({
    id: song.id,
    nameKr: song.nameKr,
    artistKr: song.artistKr || album?.artist?.nameKr || '',
    fileUrl: song.fileUrl,
    albumImage: album?.image || FALLBACK_IMG,
    albumName: album?.nameKr,
    lyrics: song.lyrics,
    syncLyrics: song.syncLyrics,
  });

  const handlePlay = (song) => {
    if (!song.fileUrl) return;
    setPlayingId(song.id);
    playImmediate([buildTrack(song)]);
  };

  const handlePlayAll = () => {
    const playable = songs.filter((s) => s.fileUrl);
    if (playable.length === 0) return;
    playImmediate(playable.map(buildTrack));
  };

  const handleAddSong = (e, song) => {
    e.stopPropagation();
    if (!song.fileUrl) return;
    addBulkToPlaylist([buildTrack(song)]);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center h-[60vh] gap-3 text-slate-400">
          <Loader2 size={22} className="animate-spin" />
          <span>로딩 중...</span>
        </div>
      </PublicLayout>
    );
  }

  if (!album) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center h-[60vh] text-slate-400">
          앨범을 찾을 수 없습니다.
        </div>
      </PublicLayout>
    );
  }

  const playableCount = songs.filter((s) => s.fileUrl).length;

  return (
    <PublicLayout>
      <SEO
        title={album.nameKr || '앨범'}
        description={`${album.nameKr} - ${album.artist?.nameKr || 'p!nk 앨범'}`}
        path={`/albums/${id}`}
        image={album.image}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'MusicAlbum',
          name: album.nameKr,
          image: album.image,
          url: `https://impink.co.kr/albums/${id}`,
          byArtist: { '@type': 'MusicGroup', name: album.artist?.nameKr },
          numTracks: songs.length,
        }}
      />
      {/* 앨범 헤더 */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{ backgroundImage: `url(${album.image || FALLBACK_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1526] via-[#0d1526]/80 to-[#0d1526]/40" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 flex items-start gap-8">
          {/* 앨범 커버 */}
          <img
            src={album.image || FALLBACK_IMG}
            alt={album.nameKr}
            className="hidden sm:block w-36 h-36 md:w-48 md:h-48 rounded-2xl object-cover shadow-2xl shrink-0 mt-14"
          />

          {/* 앨범 정보 */}
          <div className="min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-slate-400 hover:text-white text-sm mb-3 transition-colors">
              <ChevronLeft size={15} />
              뒤로
            </button>
            <p className="text-sky-400 text-xs font-medium tracking-widest uppercase mb-2">
              앨범
            </p>
            <h1 className="text-white text-3xl md:text-4xl font-bold truncate mb-1">
              {album.nameKr}
            </h1>
            <p
              onClick={() => album.artist?.id && navigate(`${PUBLIC_BASE}/artists/${album.artist.id}`)}
              className={`text-base mb-1 transition-colors ${album.artist?.id ? 'text-slate-300 cursor-pointer hover:text-sky-400' : 'text-slate-300'}`}>
              {album.artist?.nameKr}
            </p>
            {album.releaseDateTime && (
              <p className="text-slate-400 text-sm mb-1">
                {formatDate(album.releaseDateTime)} · 수록곡 {songs.length}곡
              </p>
            )}
            {album.genreId && GENRE_MAP[album.genreId] && (
              <p className="text-slate-400 text-sm mb-1">
                <span className="text-slate-500">장르 · </span>{GENRE_MAP[album.genreId]}
              </p>
            )}
            {album.label && (
              <p className="text-slate-400 text-sm mb-2">
                <span className="text-slate-500">기획사 · </span>{album.label}
              </p>
            )}
            <div className="flex items-center gap-4 mb-5">
              <LikeButton targetType="album" targetId={id} />
              <ShareButtons
                title={album.nameKr}
                description={`${album.nameKr} - ${album.artist?.nameKr || ''}`}
                image={album.image}
              />
            </div>

            {playableCount > 0 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePlayAll}
                  className="flex items-center gap-2 px-5 py-2.5 bg-sky-500 hover:bg-sky-400 rounded-full text-white font-semibold text-sm transition-colors shadow-lg">
                  <Play size={16} fill="white" />
                  전체 재생
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 수록곡 목록 */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {songs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
            <ListMusic size={40} />
            <p>수록곡이 없습니다.</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {songs.map((song, idx) => (
              <div
                key={song.id}
                onClick={() => handlePlay(song)}
                className={`group flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${
                  song.fileUrl
                    ? 'cursor-pointer hover:bg-white/5'
                    : 'opacity-50 cursor-default'
                } ${playingId === song.id ? 'bg-white/5' : ''}`}>
                {/* 트랙 번호 / 재생 아이콘 */}
                <div className="w-7 text-center shrink-0">
                  {song.fileUrl ? (
                    <>
                      <span className={`text-slate-500 text-sm group-hover:hidden ${playingId === song.id ? 'hidden' : ''}`}>
                        {song.track || idx + 1}
                      </span>
                      <Play
                        size={13}
                        className={`text-sky-400 mx-auto hidden group-hover:block ${playingId === song.id ? '!block' : ''}`}
                        fill="currentColor"
                      />
                    </>
                  ) : (
                    <span className="text-slate-600 text-sm">{song.track || idx + 1}</span>
                  )}
                </div>

                {/* 곡명 + 타이틀 배지 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm font-medium truncate ${
                        playingId === song.id
                          ? 'text-sky-400'
                          : 'text-white group-hover:text-sky-300'
                      }`}>
                      {song.nameKr}
                    </span>
                    {song.titleYn === 'Y' && (
                      <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-400 font-medium">
                        타이틀
                      </span>
                    )}
                  </div>
                </div>

                {/* 아티스트명 */}
                <span className="hidden sm:block text-xs text-slate-500 truncate w-32 md:w-44 shrink-0 text-right">
                  {song.artistKr || album?.artist?.nameKr || ''}
                </span>

                {/* 가사 버튼 */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (song.lyrics) {
                      setLyricsModal({
                        songId: song.id,
                        lyrics: song.lyrics,
                        syncLyrics: song.syncLyrics,
                        songName: song.nameKr,
                        artistName: song.artistKr || album?.artist?.nameKr || '',
                      });
                    }
                  }}
                  title="가사"
                  disabled={!song.lyrics}
                  className={`shrink-0 p-1.5 rounded-lg transition-all ${
                    song.syncLyrics
                      ? 'text-sky-400 hover:text-sky-300 hover:bg-white/5'
                      : song.lyrics
                      ? 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
                      : 'text-slate-700 cursor-default'
                  }`}>
                  <ScrollText size={15} />
                </button>

                {/* 담기 버튼 (hover 시 표시) */}
                {song.fileUrl && (
                  <button
                    onClick={(e) => handleAddSong(e, song)}
                    title="담기"
                    className="shrink-0 p-1.5 text-slate-500 hover:text-sky-400 transition-all rounded-lg hover:bg-white/5">
                    <ListPlus size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
      {/* 소개 / 크레딧 */}
      {(album.promotionDesc || album.promotionCredits) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-8 space-y-6">
          {album.promotionDesc && (
            <div>
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">소개</h2>
              <div className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">
                {album.promotionDesc}
              </div>
            </div>
          )}
          {album.promotionCredits && (
            <div>
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">크레딧</h2>
              <div className="text-slate-500 text-sm leading-relaxed whitespace-pre-line">
                {album.promotionCredits}
              </div>
            </div>
          )}
        </section>
      )}

      {/* 댓글 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-12">
        <ContentCommentSection targetType="album" targetId={id} label="댓글" />
      </div>

      {/* 가사 모달 */}
      {lyricsModal && (
        <LyricsModal
          {...lyricsModal}
          onClose={() => setLyricsModal(null)}
        />
      )}
    </PublicLayout>
  );
}

export default PublicAlbumDetailPage;
